import { PopoverPosition } from '@mui/material';
import { MapContext } from 'components/maps/MapContext';
import { ContextMenuItem } from 'components/maps/MapMarker';
import { MjolnirGestureEvent } from 'mjolnir.js';
import { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { SetState } from 'types/props';

type UseMapEntityEventsOptions<TItem = ContextMenuItem> = {
  setContextPosition: SetState<PopoverPosition | undefined>;
  setAllContextItems: SetState<TItem[]>;
  setContextMenuCoordinates: SetState<number[] | undefined>;
};

export const useMapEntityEvents = <TItem = ContextMenuItem>({
  setContextPosition,
  setAllContextItems,
  setContextMenuCoordinates,
}: UseMapEntityEventsOptions<TItem>) => {
  const { setEntity } = useContext(MapContext);
  const history = useHistory();

  const onRightClick = useCallback(
    ({ object, coordinate }, event: MjolnirGestureEvent) => {
      if (!object) {
        return;
      }
      const mouseEvent = event.srcEvent as MouseEvent;
      setContextPosition({
        top: mouseEvent.clientY,
        left: mouseEvent.clientX,
      });
      setContextMenuCoordinates(coordinate);
      setAllContextItems([...object.properties.contextMenuItems]);
    },
    [setAllContextItems, setContextMenuCoordinates, setContextPosition]
  );

  const onControlClick = useCallback(
    ({ object }, _event: MjolnirGestureEvent) => {
      if (!object?.properties.id) {
        return;
      }
      setEntity(object.properties.id);
    },
    [setEntity]
  );

  const onLeftClick = useCallback(
    ({ object }, _event: MjolnirGestureEvent) => {
      if (!object?.geometry) {
        return;
      }
      if (object.properties.entityDrawerRoute) {
        history.push(`/${object.properties.entityDrawerRoute}/${object.properties.id}`);
      }
    },
    [history]
  );

  const handleOnClick = useCallback(
    (pickingInfo, event: MjolnirGestureEvent) => {
      if (event.rightButton) {
        return onRightClick(pickingInfo, event);
      }
      if (event.srcEvent.ctrlKey) {
        return onControlClick(pickingInfo, event);
      }
      onLeftClick(pickingInfo, event);
    },
    [onControlClick, onLeftClick, onRightClick]
  );

  return { onControlClick, onLeftClick, onRightClick, handleOnClick };
};
